'use client';

import { useState } from 'react';
import { Link } from 'src/components/Link';
import { Layout } from 'src/layout';
import { MetaInfoType } from 'src/types';
import { type CatalogProduct } from 'src/types/backendContent';
import { Breadcrumbs } from 'src/uikit/Breadcrumbs';
import { Button } from 'src/uikit/Button';
import { Form } from 'src/uikit/Form';
import { Tag } from 'src/uikit/Tag';

import s from './Cases.module.scss';

interface HeaderProps {
  title: string;
  description: string;
  companyName: string;
  categories: HeaderCategoriesType[];
}

interface HeaderCategoriesType {
  id: string;
  name: string;
  slug: string;
  title: string;
}

function Header({ title, description, companyName, categories }: HeaderProps) {
  const breadcrumbs = [
    {
      title: 'Главная',
      link: '/',
    },
    {
      title: 'Кейсы',
      link: '/cases',
    },
    {
      title: companyName,
      link: '',
    },
  ];

  return (
    <div className={s.header}>
      <div>
        <Breadcrumbs links={breadcrumbs} className={s.breadcrumbs} />
      </div>
      {categories && (
        <div className={s.headerTags}>
          {categories.map((category) => {
            return (
              <Tag
                appearance={Tag.appearances['gray-90']}
                variant={Tag.variants.Primary}
                key={category.id}
              >
                {category.title}
              </Tag>
            );
          })}
        </div>
      )}
      <h1
        className={s.headerTitle}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      <p
        className={s.headerDesc}
        data-qa="page-description"
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </div>
  );
}

function Content({
  blocks,
  setModalShow,
}: {
  blocks: BlocksType[];
  setModalShow: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  return (
    <div className={s.content}>
      {blocks.map(({ title, body }, index) => (
        <div key={index} className={s.article}>
          <h2 className={s.articleTitle}>{title}</h2>
          <div
            className={s.articleContent}
            dangerouslySetInnerHTML={{ __html: body }}
          />
        </div>
      ))}
      {
        <div className={s.banner} data-attribute="no_search_index">
          <h2 className={s.bannerTitle}>
            Запросите бесплатную консультацию по вашему проекту
          </h2>
          <Button
            className="p-case__banner-button gtm-click-consultation-case"
            onClick={() => setModalShow(true)}
            analyticsOptions={{
              action: 'click',
              clickZone: 'body',
              clickElement: 'button',
              clickContent: 'Связаться с нами',
              uniqueId: 'case-detailed-main',
              transitionType: 'inside-link',
            }}
          >
            Связаться с нами
          </Button>
          <img
            src="https://cdn.cloud.ru/backend/cases/cloud-banner.png"
            alt="Cloud"
            className={s.bannerImg}
            data-qa="cloud icon"
            width={725}
            height={500}
          />
        </div>
      }
    </div>
  );
}

interface BlocksType {
  title: string;
  body: string;
}

export interface CaseFull {
  id: string;
  slug: string;
  title: string;
  companyName: string;
  description: string;
  image: string | null;
  metaInformation: MetaInfoType | null;
  blocks: BlocksType[];
  categories: HeaderCategoriesType[];
  products?: CatalogProduct[];
}

function CaseItem({ product }: { product: CatalogProduct }) {
  const iconToUse = product.icon
    ? product.icon
    : '/svg/components/ProductsCatalog/icon.svg';

  let link = '';
  if (product.slug) link = `/products/${product.slug}`;
  if (product.customLink) link = `/${product.customLink}`;

  return (
    <Link
      key={product.id}
      href={link}
      className={s.productsItem}
      rel="noreferrer"
      id={`cases-item-link-${product.title}`}
      analyticsOptions={{
        action: link,
        clickZone: 'body',
        clickElement: 'textclick',
        clickContent: product.title,
        uniqueId: `cases-item-link-${product.title}`,
        transitionType: 'inside-link',
      }}
    >
      <img
        src={iconToUse}
        className={s.productsImg}
        alt="Product logo"
        data-qa="Product logo"
        width={88}
        height={88}
      />
      <p className={s.productsTitle}>{product.title}</p>
    </Link>
  );
}

function CaseProducts({ products }: { products: CatalogProduct[] }) {
  return (
    <div className={s.products}>
      {products.map((product, index: number) => (
        <CaseItem key={index} product={product} />
      ))}
    </div>
  );
}

export function CasePage(props: CaseFull) {
  const [modalShow, setModalShow] = useState(false);

  return (
    <Layout>
      <div className={s.root}>
        <div className={s.main}>
          <div className={s.mainContent}>
            <Header
              companyName={props.companyName}
              title={props.title}
              description={props.description}
              categories={props.categories}
            />
            <Content setModalShow={setModalShow} blocks={props.blocks} />
          </div>
          {props.products && <CaseProducts products={props.products} />}
        </div>

        {modalShow && (
          <Form
            title="Связаться с нашим специалистом"
            inputPath="v2/order"
            additionalFields={{ subsegment: 'consultation' }}
            allFormsSentFormType="consultation"
            allFormsSentIsPopup
            modalClose={() => setModalShow(false)}
            analyticsFieldFill={{
              formType: 'popup',
              popupPlace: 'body',
              buttonId: 'case-detailed-main',
            }}
            formVariant="modal"
            successfulSubmissonFormAnalytics={{
              formname: 'consultation',
            }}
          />
        )}
      </div>
    </Layout>
  );
}
